import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type PassType      = 'Outgoing' | 'Incoming Late' | 'Emergency Exit' | 'Medical' | 'Event' | 'Half Day';
export type PassStatus    = 'Pending' | 'Approved' | 'Rejected' | 'Active' | 'Completed' | 'Expired' | 'Cancelled';
export type GatePassTab   = 'overview' | 'issue' | 'active' | 'requests' | 'log';

export interface GatePassRecord {
  id:              number;
  pass_no:         string;
  student_id:      number;
  student_name:    string;
  class_name:      string;
  section:         string;
  roll_no:         string;
  parent_mobile:   string;
  pass_type:       PassType;
  reason:          string;
  reg_date:        string;
  out_time:        any;          // approved out time
  in_time:         any;          // approved return time
  actual_out_time: any;          // scanned at gate
  actual_in_time:  any;          // scanned at gate
  requested_by:    string;          // 'parent' | 'student' | 'teacher' | 'admin'
  contact_person:  string;          // who is taking the student
  contact_mobile:  string;
  approved_by:     number;
  approved_at:     any;
  rejected_reason: string;
  remarks:         string;
  pass_status:     PassStatus;
  parent_notified: boolean;
  created_at:      any;
}

export interface GateLog {
  id:           number;
  pass_id:      number;
  pass_no:      string;
  student_name: string;
  class_name:   string;
  io_event:     'OUT' | 'IN';
  io_time:      string;
  scanned_by:   number;
  remarks:      string;
}

@Component({
  selector:    'app-gate-pass',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './gate-pass.html',
  styleUrl:    './gate-pass.scss',
})
export class GatePass implements OnInit {
  dataService = inject(DataService);
  loading = false;

  activeTab        = signal<GatePassTab>('overview');
  saveSuccess      = signal('');
  showDeleteModal  = signal(false);
  deleteId         = signal<number | null>(null);
  showPassModal    = signal(false);
  viewingPass      = signal<GatePassRecord | null>(null);
  showScanModal    = signal(false);
  scanningPass     = signal<GatePassRecord | null>(null);

  // Issue form
  issueForm: Partial<GatePassRecord> & { search_student?: string } = {};
  issueSubmitted   = false;
  issueErrors: Record<string, string> = {};
  studentSuggestions: { id: number; name:string; class_name:string; section:string; roll_no:string; parent_mobile:string }[] = [];

  // Filters
  reqSearch   = signal('');
  reqStatus   = signal<PassStatus | ''>('Pending');
  reqType     = signal<PassType | ''>('');
  reqDate     = signal('');

  activeSearch = signal('');

  logSearch   = signal('');
  logDate     = signal('');
  logEvent    = signal<'OUT' | 'IN' | ''>('');

  // Approve/Reject state
  approveRejectId   = signal<number | null>(null);
  rejectReason      = '';
  approveRejectMode = signal<'approve' | 'reject' | null>(null);

  // Scan
  scanRemark = '';

  private nextPassId  = 1;
  private passCounter = 1;
  private nextLogId   = 1;

  // ── LOOKUP DATA ──────────────────────────────────────
  readonly passTypes: PassType[]      = ['Outgoing','Incoming Late','Emergency Exit','Medical','Event','Half Day'];
  readonly requestedByOpts            = ['parent','student','teacher','admin'];
  // readonly classOptions               = this.dataService.classes;

  readonly today = new Date().toISOString().substring(0, 10);
  readonly nowTime = new Date().toTimeString().substring(0, 5);

  studentDB: any = [];

  // ── DATA ─────────────────────────────────────────────
  passes   = signal<GatePassRecord[]> ([]);
  gateLogs = signal<GateLog[]> ([]);

  // ── COMPUTED ─────────────────────────────────────────
  readonly stats = computed(() => {
    const p = this.passes();
    const today = new Date().toISOString().substring(0, 10);
    return {
      pending:   p.filter(x => x.pass_status === 'Pending').length,
      active:    p.filter(x => x.pass_status === 'Active').length,
      today:     p.filter(x => x.reg_date === today).length,
      outNow:    p.filter(x => x.pass_status === 'Active' && x.actual_out_time && !x.actual_in_time).length,
      completed: p.filter(x => x.pass_status === 'Completed').length,
      total:     p.length,
    };
  });

  readonly filteredRequests = computed(() => {
    const q = this.reqSearch().toLowerCase();
    return this.passes()
      .filter(p => {
        const mQ = !q || [p.student_name, p.pass_no, p.class_name, p.reason].some(v => v.toLowerCase().includes(q));
        const mS = !this.reqStatus() || p.pass_status === this.reqStatus();
        const mT = !this.reqType()   || p.pass_type === this.reqType();
        const mD = !this.reqDate()   || p.reg_date === this.reqDate();
        return mQ && mS && mT && mD;
      })
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  });

  readonly activePasses = computed(() => {
    const q = this.activeSearch().toLowerCase();
    return this.passes()
      .filter(p =>
        p.pass_status === 'Active' &&
        (!q || [p.student_name, p.pass_no, p.class_name].some(v => v.toLowerCase().includes(q)))
      )
      .sort((a, b) => b.approved_at.localeCompare(a.approved_at));
  });

  readonly filteredLogs = computed(() => {
    const q = this.logSearch().toLowerCase();
    return this.gateLogs()
      .filter(l => {
        const mQ = !q || [l.student_name, l.pass_no, l.class_name].some(v => v.toLowerCase().includes(q));
        const mD = !this.logDate()  || l.io_time.startsWith(this.logDate());
        const mE = !this.logEvent() || l.io_event === this.logEvent();
        return mQ && mD && mE;
      })
      .sort((a, b) => b.io_time.localeCompare(a.io_time));
  });

  readonly todayTimeline = computed(() => {
    const today = new Date().toISOString().substring(0, 10);
    const retVal: any = this.passes()
      // .filter(p => {
      //   p.reg_date === today;
      //   console.log(p.reg_date + '==' + today);
      // })
      // .sort((a, b) => 
      //   b.created_at.localeCompare(a.created_at)
      // );

      if(retVal && retVal.length) {
      return retVal;
    }
  });

  async ngOnInit() {
    this.loading = true;
    await this.dataService.getAllRecords('admissions').then((res: any) => {
      if(res && res.length) {
        for(let i=0; i<res.length; i++) {
          res[i].f_info = this.dataService.str2json(res[i].f_info);

          const std: any = { 
            id: res[i].id, 
            name: res[i].std_name,
            class_name: res[i].class_name,
            section: res[i].section,
            roll_no: res[i].roll_no,
            parent_mobile: res[i].f_info?.mobile
          };
          this.studentDB.push(std);
        }
      }

      this.loading = false;
    });

    this.loading = true;
    await this.dataService.getAllRecords('gate_pass_rec').then((res: any) => {
      if(res && res.length) {
        this.passes.set(res);
        this.nextPassId  = parseInt(res[0].pass_no.substr(-4)) + 1;
        this.passCounter = parseInt(res[0].pass_no.substr(-4));
      }

      this.loading = false;
    });

    this.loading = true;
    await this.dataService.getAllRecords('gate_pass_log').then((res: any) => {
      if(res && res.length) {
        this.gateLogs.set(res);
      }

      this.loading = false;
    });

  }

  // ── STUDENT SEARCH ────────────────────────────────────
  onStudentSearch(): void {
    const q = (this.issueForm.search_student ?? '').toLowerCase().trim();
    if (!q || q.length < 2) { this.studentSuggestions = []; return; }
    this.studentSuggestions = this.studentDB.filter((s: any) =>
      s.name.toLowerCase().includes(q) || s.id == q || s.roll_no.toLowerCase().includes(q)
    ).slice(0, 5);

    // if(this.studentSuggestions && this.studentSuggestions.length==0) {
    //   this.studentSuggestions = [{ id: 0, name: 'Visitor: ' + q, class_name: '',  section:'', roll_no:'', parent_mobile:'' }];
    // }
  }

  selectStudent(s: typeof this.studentDB[0]): void {
    this.issueForm = {
      ...this.issueForm,
      student_id:    s.id,
      student_name:  s.name,
      class_name:    s.class_name,
      section:       s.section,
      roll_no:       s.roll_no,
      parent_mobile: s.parent_mobile,
      search_student:'',
    };
    this.studentSuggestions = [];
  }

  // ── ISSUE GATE PASS ───────────────────────────────────
  openIssueForm(): void {
    this.issueForm = {
      search_student:   '',
      pass_type:        'Outgoing',
      reg_date:         this.today,
      out_time:         this.nowTime,
      in_time:          '',
      reason:           '',
      requested_by:     'parent',
      contact_person:   '',
      contact_mobile:   '',
      remarks:          '',
    };
    this.issueSubmitted = false;
    this.issueErrors    = {};
    this.studentSuggestions = [];
    this.activeTab.set('issue');
  }

  submitIssue(autoApprove = false): void {
    this.issueSubmitted = true;
    this.issueErrors    = {};
    if (!this.issueForm.student_name)  this.issueErrors['student']   = 'Please select/enter name.';
    if (!this.issueForm.pass_type)   this.issueErrors['pass_type'] = 'Pass type is required.';
    if (!this.issueForm.reason?.trim()) this.issueErrors['reason'] = 'Reason is required.';
    if (!this.issueForm.reg_date)        this.issueErrors['date']      = 'Date is required.';
    if (!this.issueForm.out_time)    this.issueErrors['out_time']  = 'Out time is required.';
    if (Object.keys(this.issueErrors).length) return;

    const now1     = new Date().toISOString();
    const now2 = new Date().toLocaleTimeString();

    const passNo  = `GP-${new Date().getFullYear()}-${String(this.passCounter + 1).padStart(4, '0')}`;
    this.passCounter++;

    const newPass: GatePassRecord = {
      id:               this.nextPassId++,
      pass_no:          passNo,
      student_id:       this.issueForm.student_id!,
      student_name:     this.issueForm.student_name!,
      class_name:       this.issueForm.class_name!,
      section:          this.issueForm.section ?? '',
      roll_no:          this.issueForm.roll_no ?? '',
      parent_mobile:    this.issueForm.parent_mobile ?? '',
      pass_type:        this.issueForm.pass_type!,
      reason:           this.issueForm.reason!.trim(),
      reg_date:         this.issueForm.reg_date!,
      out_time:         this.issueForm.out_time ?? null,
      in_time:          this.issueForm.in_time ?? null,
      actual_out_time:  null,
      actual_in_time:   null,
      requested_by:     this.issueForm.requested_by ?? 'admin',
      contact_person:   this.issueForm.contact_person ?? '',
      contact_mobile:   this.issueForm.contact_mobile ?? '',
      approved_by:      autoApprove ? 1 : 0,
      approved_at:      autoApprove ? now2 : null,
      rejected_reason:  '',
      remarks:          this.issueForm.remarks ?? '',
      pass_status:      autoApprove ? 'Approved' : 'Pending',
      parent_notified:  false,
      created_at:       now1,
    };

    this.doSave('gate_pass_rec', newPass);

    this.passes.update(l => [newPass, ...l]);
    this.flash(`Gate pass ${passNo} ${autoApprove ? 'issued and approved' : 'submitted for approval'}!`);
    this.openIssueForm();
    this.activeTab.set('requests');
  }

  // ── APPROVE / REJECT ──────────────────────────────────
  openApprove(pass: GatePassRecord): void {
    this.approveRejectId.set(pass.id);
    this.approveRejectMode.set('approve');
    this.rejectReason = '';
  }

  openReject(pass: GatePassRecord): void {
    this.approveRejectId.set(pass.id);
    this.approveRejectMode.set('reject');
    this.rejectReason = '';
  }

  confirmApprove(): void {
    const id = this.approveRejectId();
    if (!id) return;
    const now = new Date().toISOString();
    this.passes.update(l => l.map(p => p.id === id
      ? { ...p, pass_status: 'Approved', approved_by: 1, approved_at: now, rejected_reason: '' }
      : p
    ));

    this.passes().filter((obj: any) => {
      if(obj.id == id) {
        this.doUpdate('gate_pass_rec', obj);
      }
    });

    this.approveRejectId.set(null); this.approveRejectMode.set(null);
    this.flash('Gate pass approved!');
  }

  confirmReject(): void {
    if (!this.rejectReason.trim()) return;
    const id = this.approveRejectId();
    if (!id) return;
    const now = new Date().toISOString();
    this.passes.update(l => l.map(p => p.id === id
      ? { ...p, pass_status: 'Rejected', rejected_reason: this.rejectReason, approved_by: 1, approved_at: now }
      : p
    ));

    this.passes().filter((obj: any) => {
      if(obj.id == id) {
        this.doUpdate('gate_pass_rec', obj);
      }
    });

    this.approveRejectId.set(null); this.approveRejectMode.set(null);
    this.flash('Gate pass rejected.');
  }

  // ── SCAN / MARK GATE ──────────────────────────────────
  openScan(pass: GatePassRecord): void {
    this.scanningPass.set({ ...pass });
    this.scanRemark = '';
    this.showScanModal.set(true);
  }

  markOut(): void {
    const p = this.scanningPass();
    if (!p) return;
    const now = new Date().toISOString();
    // const now = new Date().toLocaleTimeString();

    const log: GateLog = { id: this.nextLogId++, pass_id: p.id, pass_no: p.pass_no, student_name: p.student_name, class_name: p.class_name + '-' + p.section, io_event:'OUT', io_time: now, scanned_by: 0, remarks: this.scanRemark };

    this.passes.update(l => l.map(x => x.id === p.id ? { ...x, pass_status:'Active', actual_out_time: now } : x));
    this.gateLogs.update(l => [log, ...l]);

    this.passes().filter((obj: any) => {
      if(obj.id == p.id) {
        this.doUpdate('gate_pass_rec', obj);
        this.doSave('gate_pass_log', log);
      }
    });

    this.showScanModal.set(false);
    this.flash(`${p.student_name} marked OUT at gate.`);
  }

  markIn(): void {
    const p = this.scanningPass();
    if (!p) return;
    const now = new Date().toISOString();
    // const now = new Date().toLocaleTimeString();
    const log: GateLog = { id: this.nextLogId++, pass_id: p.id, pass_no: p.pass_no, student_name: p.student_name, class_name: p.class_name + '-' + p.section, io_event:'IN', io_time: now, scanned_by: 0, remarks: this.scanRemark };

    this.passes.update(l => l.map(x => x.id === p.id ? { ...x, pass_status:'Completed', actual_in_time: now } : x));
    this.gateLogs.update(l => [log, ...l]);

    this.passes().filter((obj: any) => {
      if(obj.id == p.id) {
        this.doUpdate('gate_pass_rec', obj);
        this.doSave('gate_pass_log', log);
      }
    });

    this.showScanModal.set(false);
    this.flash(`${p.student_name} marked IN at gate.`);
  }

  notifyParent(pass: GatePassRecord): void {
    this.passes.update(l => l.map(p => p.id === pass.id ? { ...p, parent_notified: true } : p));

    this.passes().filter((obj: any) => {
      if(obj.id == pass.id) {
        this.doUpdate('gate_pass_rec', obj);
      }
    });

    this.flash(`SMS sent to parent of ${pass.student_name}.`);
  }

  cancelPass(id: number): void {
    if (!confirm('Cancel this gate pass?')) return;
    this.passes.update(l => l.map(p => p.id === id ? { ...p, pass_status: 'Cancelled' } : p));

    this.passes().filter((obj: any) => {
      if(obj.id == id) {
        this.doUpdate('gate_pass_rec', obj);
      }
    });

    this.flash('Gate pass cancelled.');
  }

  // ── VIEW PASS ────────────────────────────────────────
  viewPass(p: GatePassRecord): void { this.viewingPass.set(p); this.showPassModal.set(true); }

  printPass(): void { window.print(); }

  // ── HELPERS ──────────────────────────────────────────
  getStatusClass(s: PassStatus): string {
    return { Pending:'st-pending', Approved:'st-approved', Rejected:'st-rejected', Active:'st-active', Completed:'st-done', Expired:'st-expired', Cancelled:'st-cancelled' }[s] ?? '';
  }
  getTypeClass(t: PassType): string {
    return { Outgoing:'tp-out', 'Incoming Late':'tp-in', 'Emergency Exit':'tp-emergency', Medical:'tp-medical', Meeting:'tp-meeting', Event:'tp-event', 'Half Day':'tp-half' }[t] ?? '';
  }
  getTypeIcon(t: PassType): string {
    return { Outgoing:'bi-arrow-right-circle-fill', 'Incoming Late':'bi-arrow-left-circle-fill', 'Emergency Exit':'bi-exclamation-octagon-fill', Medical:'bi-heart-pulse-fill', Meeting:'bi-people-fill', Event:'bi-calendar-event-fill', 'Half Day':'bi-clock-fill' }[t] ?? 'bi-pass-fill';
  }
  getStatusIcon(s: PassStatus): string {
    return { Pending:'bi-hourglass-split', Approved:'bi-check-circle-fill', Rejected:'bi-x-circle-fill', Active:'bi-record-circle-fill', Completed:'bi-check2-all', Expired:'bi-clock-history', Cancelled:'bi-slash-circle-fill' }[s] ?? 'bi-circle';
  }

  formatDate(iso: string): string { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }); }
  formatDateTime(iso: string): string { if (!iso) return '—'; return new Date(iso).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true }); }
  formatTime(iso: string): string { if (!iso) return '—'; return new Date(iso).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true }); }
  formatTimeStr(t: string): string { if (!t) return '—'; const [h,m] = t.split(':'); const hr = Number(h); return `${hr%12||12}:${m} ${hr>=12?'PM':'AM'}`; }
  getInitials(name: string): string { return name.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase(); }

  calcDuration(outISO: string, inISO: string): string {
    if (!outISO || !inISO) return '—';
    const diff = Math.floor((new Date(inISO).getTime() - new Date(outISO).getTime()) / 60000);
    if (diff < 0) return '—';
    if (diff < 60) return `${diff} min`;
    return `${Math.floor(diff/60)}h ${diff%60}m`;
  }

  isOverdue(p: GatePassRecord): boolean {
    if (p.pass_status !== 'Active' || !p.in_time || !p.reg_date) return false;
    const due = new Date(`${p.reg_date}T${p.in_time}`);
    return new Date() > due;
  }

  private flash(m: string): void { this.saveSuccess.set(m); setTimeout(() => this.saveSuccess.set(''), 2800); }
  hasErrors(): boolean { return Object.keys(this.issueErrors).length > 0; }

  resetFilters(): void { this.reqSearch.set(''); this.reqStatus.set('Pending'); this.reqType.set(''); this.reqDate.set(''); }

  doSave(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
    this.dataService.addNewRecord(api_name, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate(api_name: string, frmData: any) {
    this.loading = true;
    // this.submitSuccess = true;
		const myid = frmData.id;
		delete frmData.id;

    this.dataService.updateRecord(api_name, myid, frmData).subscribe({
      next: (response) => {
        // this.submitSuccess = false;
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doDelete(api_name: string, myid: any) {
		this.loading = true;
    this.dataService.deleteRecord(api_name, myid).subscribe({
      next: (response) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}

}
